import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import exifr from "exifr";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LangInput from "@/components/LangInput";
import VoiceNoteButton from "@/components/VoiceNoteButton";
import {
  Article, LinkSimple, Camera, VideoCamera, MicrophoneStage, FileText,
  MapPin, Sparkle, CheckCircle, X, Question, WarningCircle, ArrowRight,
} from "@phosphor-icons/react";

// One card per story-type quick-start
const TYPES = [
  { key: "observation", label: "Observation", icon: MapPin, hint: "Something you saw in nature" },
  { key: "personal_story", label: "Personal story", icon: Article, hint: "An experience or long-form" },
  { key: "photo_story", label: "Photo story", icon: Camera, hint: "One or more photos + words" },
  { key: "news_link", label: "News link", icon: LinkSimple, hint: "A useful news article" },
  { key: "research", label: "Research", icon: Article, hint: "A scientific paper or study" },
  { key: "video", label: "Video", icon: VideoCamera, hint: "YouTube / Vimeo / uploaded" },
  { key: "audio", label: "Audio", icon: MicrophoneStage, hint: "A field recording or podcast" },
  { key: "document", label: "Document", icon: FileText, hint: "PDF / bulletin / report" },
  { key: "nature_memory", label: "Nature memory", icon: Article, hint: "How things used to be" },
  { key: "environmental_warning", label: "Warning", icon: WarningCircle, hint: "Report something urgent" },
  { key: "question", label: "Question", icon: Question, hint: "Ask the community" },
  { key: "conservation_story", label: "Conservation win", icon: Sparkle, hint: "Good news for nature" },
];

const readAsDataURL = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

export default function CreateStory() {
  const { user, requireSignIn } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);

  const [step, setStep] = useState(0); // 0 = pick type, 1 = compose, 2 = success
  const [storyType, setStoryType] = useState("observation");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [links, setLinks] = useState([{ url: "", link_type: "webpage", note: "" }]);
  const [media, setMedia] = useState([]);   // [{url, media_type, caption}]
  const [uploading, setUploading] = useState(false);
  const [loc, setLoc] = useState({ country: "", state: "", city: "", lat: "", lon: "", scope: "country", privacy: "exact" });
  const [locResolving, setLocResolving] = useState(false);
  const [observedAt, setObservedAt] = useState("");
  const [tags, setTags] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [analyzingLink, setAnalyzingLink] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [aiConfidence, setAiConfidence] = useState(null);
  const [published, setPublished] = useState(null);

  useEffect(() => {
    if (!user) return;
    http.get("/nature/categories", { params: { as_tree: false } })
      .then(({ data }) => {
        const all = data?.categories || [];
        const parents = new Set(all.map((c) => c.parent).filter(Boolean));
        setCategories(all.filter((c) => !parents.has(c.slug)).sort((a, b) => (a.label || "").localeCompare(b.label || "")));
      })
      .catch(() => setCategories([]));
  }, [user]);

  const pickType = (t) => { setStoryType(t); setStep(1); };

  // -------- Photo pick w/ EXIF ----------
  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files.slice(0, 8)) {
        if (file.size > 12 * 1024 * 1024) { toast.error(`${file.name} too large (12 MB max)`); continue; }
        const url = await readAsDataURL(file);
        try {
          const meta = await exifr.parse(file, { gps: true, tiff: true, exif: true });
          if (meta?.latitude && meta?.longitude && !loc.lat) {
            setLoc((L) => ({ ...L, lat: meta.latitude.toFixed(6), lon: meta.longitude.toFixed(6) }));
            resolveLocation(meta.latitude, meta.longitude);
          }
          if ((meta?.DateTimeOriginal || meta?.CreateDate) && !observedAt) {
            const d = meta.DateTimeOriginal || meta.CreateDate;
            if (d instanceof Date && !isNaN(d.getTime())) setObservedAt(d.toISOString().slice(0, 10));
          }
        } catch { /* no EXIF */ }
        const { data: up } = await http.post("/uploads/photo", { image_base64: url });
        setMedia((m) => [...m, { url: up.url, media_type: "image", caption: "" }]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  // -------- Location ----------
  const resolveLocation = async (lat, lon) => {
    setLocResolving(true);
    try {
      const { data } = await http.post("/nature/geo/lookup", { lat, lon });
      setLoc((L) => ({ ...L, country: data?.country || "", state: data?.state || "", city: data?.city || data?.village || "" }));
    } catch {} finally { setLocResolving(false); }
  };
  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition((p) => {
      setLoc((L) => ({ ...L, lat: p.coords.latitude.toFixed(6), lon: p.coords.longitude.toFixed(6) }));
      resolveLocation(p.coords.latitude, p.coords.longitude);
    }, () => toast.error("Permission denied"));
  };

  // -------- Analyze a news link → prefill title/body/category/tags/location ----------
  const analyzeFirstLink = async () => {
    const first = links[0]?.url?.trim();
    if (!first) return toast.error("Paste a link first");
    setAnalyzingLink(true);
    try {
      const { data } = await http.post("/nature/stories/analyze", { url: first });
      if (data?.headline && !title) setTitle(data.headline);
      if (data?.summary && !body) setBody(data.summary);
      if (data?.category && !category) setCategory(data.category);
      if (Array.isArray(data?.tags) && !tags) setTags(data.tags.slice(0, 6).join(", "));
      if (data?.location && !loc.country) {
        setLoc((L) => ({
          ...L,
          country: data.location.country || L.country,
          state: data.location.state || L.state,
          city: data.location.city || L.city,
          scope: data.location.scope || L.scope,
        }));
      }
      setAiConfidence(typeof data?.extraction_confidence === "number" ? data.extraction_confidence : null);
      toast.success("Link analysed — review and edit before publishing.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyse link");
    } finally { setAnalyzingLink(false); }
  };

  const removeMedia = (i) => setMedia((m) => m.filter((_, idx) => idx !== i));
  const updateLink = (i, patch) => setLinks((L) => L.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLink = () => setLinks((L) => [...L, { url: "", link_type: "webpage", note: "" }]);
  const removeLink = (i) => setLinks((L) => L.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!requireSignIn("share a story")) return;
    const hasBody = body.trim().length >= 3;
    const cleanLinks = links.filter((l) => l.url && l.url.trim());
    const hasLinks = cleanLinks.length > 0;
    const hasMedia = media.length > 0;
    if (!hasBody && !hasLinks && !hasMedia) return toast.error("Add a story, a link, or a photo — at least one.");
    if (!title.trim() && !hasBody && !hasLinks) return toast.error("Give the story a short title.");

    setSubmitting(true);
    try {
      const payload = {
        story_type: storyType,
        title: title.trim() || undefined,
        body: hasBody ? body.trim() : undefined,
        category: category || undefined,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        location: {
          country: loc.country || undefined,
          state: loc.state || undefined,
          city: loc.city || undefined,
          lat: loc.lat ? parseFloat(loc.lat) : undefined,
          lon: loc.lon ? parseFloat(loc.lon) : undefined,
          scope: loc.scope,
          privacy: loc.privacy,
        },
        observed_at: observedAt || undefined,
        media,
        links: cleanLinks.map((l) => ({ url: l.url.trim(), link_type: l.link_type, note: l.note || undefined })),
      };
      const { data } = await http.post("/nature/stories/create-story", payload);
      setPublished(data);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  // ---------- SUCCESS ----------
  useEffect(() => {
    if (step === 2) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  if (step === 2 && published) {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="create-story-success">
        <div className="max-w-[720px] mx-auto rounded-2xl bg-white border border-black/10 p-8 md:p-10 shadow-sm text-[#1B4332]">
          <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mb-3">
            <CheckCircle size={26} weight="fill" className="text-emerald-600" />
          </div>
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            {published.moderation_status === "approved" ? "Published" : "Submitted for review"}
          </div>
          <h2 className="mt-1 font-serif-display text-3xl leading-tight">{published.title}</h2>
          {published.body && <p className="mt-2 text-sm text-[#3A3830] line-clamp-4">{published.body}</p>}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => nav(`/stories/${published.id}`)}
              data-testid="create-story-view"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase"
            >
              View story <ArrowRight size={12} weight="bold" />
            </button>
            <button
              onClick={() => { setStep(0); setPublished(null); setTitle(""); setBody(""); setMedia([]); setLinks([{ url: "", link_type: "webpage", note: "" }]); setTags(""); }}
              className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase"
              data-testid="create-story-another"
            >
              Share another
            </button>
          </div>
        </div>
      </main>
    );
  }

  // ---------- STEP 0 · pick a type ----------
  if (step === 0) {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="create-story-picker">
        <div className="max-w-[1000px] mx-auto">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            / Share a nature story
          </div>
          <h1 className="mt-2 font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
            What would you like to share?
          </h1>
          <p className="mt-3 font-serif-display text-base md:text-lg italic text-[#3A3830] max-w-2xl">
            Photos are optional — a memory, a link, a question or a two-line observation is enough. Pick a starting point:
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => pickType(t.key)}
                  data-testid={`create-type-${t.key}`}
                  className="text-left rounded-2xl border border-black/10 bg-white/70 hover:bg-white hover:border-[color:var(--np-brand,#B85F2A)] p-5 transition-colors flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-[color:var(--np-brand,#B85F2A)]/10 grid place-items-center text-[color:var(--np-brand,#B85F2A)]">
                    <Icon size={20} weight="fill" />
                  </div>
                  <div>
                    <div className="font-serif-display text-lg leading-tight">{t.label}</div>
                    <div className="mt-1 text-xs text-[#3A3830]">{t.hint}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    );
  }

  // ---------- STEP 1 · compose ----------
  const showLinkAnalyze = ["news_link", "useful_link", "research", "government_information", "video"].includes(storyType);

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="create-story-form">
      <div className="max-w-[820px] mx-auto">
        <button
          onClick={() => setStep(0)}
          className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:opacity-80"
        >← Change type</button>
        <h1 className="mt-2 font-serif-display text-3xl md:text-5xl leading-[1.02] tracking-tight">
          Tell us what happened.
        </h1>
        <p className="mt-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">
          {TYPES.find((t) => t.key === storyType)?.label}
        </p>

        <div className="mt-6 rounded-2xl bg-white border border-black/10 p-5 md:p-7 shadow-sm text-[#1B4332] space-y-5">
          {/* Paste-a-link → auto-fill (elevated so it's the first thing users see) */}
          <div
            className="rounded-xl border border-dashed border-[color:var(--np-brand,#B85F2A)]/50 bg-[#FBF2E1]/60 p-4"
            data-testid="cs-link-expand-box"
          >
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" /> Have a link? Auto-fill the form
              </div>
              {aiConfidence != null && (
                <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)]">
                  AI · {Math.round(aiConfidence * 100)}%
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-[#3A3830]">
              Paste a webpage, news article or video URL. We'll extract the title, summary, category and location so you can review and edit before publishing.
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <input
                value={links[0]?.url || ""}
                onChange={(e) => updateLink(0, { url: e.target.value })}
                placeholder="https://…"
                data-testid="cs-link-expand-url"
                className="flex-1 min-w-[240px] bg-white border border-black/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
              />
              <button
                type="button"
                onClick={analyzeFirstLink}
                disabled={analyzingLink || !links[0]?.url?.trim()}
                data-testid="cs-link-expand-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#B85F2A] text-white px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50 hover:opacity-90"
              >
                <Sparkle size={12} weight="fill" />
                {analyzingLink ? "Expanding…" : "Expand & auto-fill"}
              </button>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Title</label>
            <div className="mt-1">
              <LangInput
                as="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give it a short, factual title"
                testid="cs-title"
                className="text-base"
              />
            </div>
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">
                Your story {storyType === "question" ? "· question" : ""}
              </label>
              <VoiceNoteButton
                testid="cs-voice"
                onTranscript={(text) => setBody((b) => (b ? b.trimEnd() + " " : "") + text)}
              />
            </div>
            <div className="mt-1">
              <LangInput
                as="textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                placeholder="What did you see, experience, remember, learn or discover? Why does it matter?"
                testid="cs-body"
              />
            </div>
          </div>

          {/* Links */}
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">
              Useful links {showLinkAnalyze && <span className="ml-2 text-[color:var(--np-brand,#B85F2A)] normal-case">· primary source</span>}
            </label>
            <div className="mt-1 space-y-2">
              {links.map((L, i) => (
                <div key={i} className="rounded-lg border border-black/10 bg-[#FBF2E1]/40 p-3 space-y-2">
                  <div className="flex gap-2">
                    <input
                      value={L.url}
                      onChange={(e) => updateLink(i, { url: e.target.value })}
                      placeholder="https://…"
                      data-testid={`cs-link-url-${i}`}
                      className="flex-1 bg-white border border-black/15 rounded px-3 py-2 text-sm"
                    />
                    <select
                      value={L.link_type}
                      onChange={(e) => updateLink(i, { link_type: e.target.value })}
                      className="bg-white border border-black/15 rounded px-2 text-xs"
                      data-testid={`cs-link-type-${i}`}
                    >
                      <option value="webpage">Webpage</option>
                      <option value="news">News</option>
                      <option value="research">Research</option>
                      <option value="government">Government</option>
                      <option value="video">Video</option>
                      <option value="podcast">Podcast</option>
                      <option value="scientific_publication">Paper</option>
                      <option value="educational">Educational</option>
                      <option value="other">Other</option>
                    </select>
                    {links.length > 1 && (
                      <button onClick={() => removeLink(i)} className="text-[#3A3830] hover:text-[#1B4332]" aria-label="Remove"><X size={14} /></button>
                    )}
                  </div>
                  <input
                    value={L.note}
                    onChange={(e) => updateLink(i, { note: e.target.value })}
                    placeholder="Why is this link important?"
                    className="w-full bg-white border border-black/15 rounded px-3 py-1.5 text-xs"
                    data-testid={`cs-link-note-${i}`}
                  />
                </div>
              ))}
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button" onClick={addLink}
                  className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]"
                  data-testid="cs-add-link"
                >+ Add another link</button>
                {showLinkAnalyze && links[0]?.url && (
                  <button
                    type="button"
                    onClick={analyzeFirstLink}
                    disabled={analyzingLink}
                    data-testid="cs-analyze-link"
                    className="inline-flex items-center gap-1 rounded-full border border-black/15 px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase bg-white hover:bg-black/5 disabled:opacity-60"
                  >
                    <Sparkle size={11} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                    {analyzingLink ? "Analysing…" : "AI prefill from link"}
                  </button>
                )}
                {aiConfidence != null && (
                  <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] self-center">
                    AI · {Math.round(aiConfidence * 100)}%
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Photos */}
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Photos (optional)</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {media.map((m, i) => (
                <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-black/10">
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeMedia(i)} className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-1" aria-label="Remove"><X size={10} weight="bold" /></button>
                </div>
              ))}
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="cs-add-photo"
                className="w-24 h-24 rounded-lg border-2 border-dashed border-black/25 grid place-items-center text-[color:var(--np-brand,#B85F2A)] hover:bg-[#FBF2E1] disabled:opacity-60"
              >
                <Camera size={22} weight="fill" />
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} className="hidden" data-testid="cs-file-input" />
            {uploading && <div className="mt-1 text-xs text-[#3A3830]">Uploading…</div>}
          </div>

          {/* Location + date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] flex items-center gap-1">
                <MapPin size={11} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> Where
              </label>
              <div className="mt-1 flex items-center gap-2">
                <div className="flex-1 rounded-lg bg-white border border-black/15 px-3 py-2 text-sm">
                  {locResolving ? "Resolving…" : ([loc.city, loc.state, loc.country].filter(Boolean).join(", ") || "No location")}
                </div>
                <button onClick={useMyLocation} className="rounded-lg border border-black/15 px-3 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase" data-testid="cs-loc-mine">
                  Use mine
                </button>
              </div>
              <select
                value={loc.privacy}
                onChange={(e) => setLoc((L) => ({ ...L, privacy: e.target.value }))}
                data-testid="cs-loc-privacy"
                className="mt-2 w-full bg-white border border-black/15 rounded px-2 py-1.5 text-xs"
              >
                <option value="exact">Exact location</option>
                <option value="approximate">Approximate</option>
                <option value="city">City / town only</option>
                <option value="region">Region only</option>
                <option value="country">Country only</option>
                <option value="private">Private (don't show)</option>
              </select>
            </div>
            <div>
              <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">When observed</label>
              <input value={observedAt} onChange={(e) => setObservedAt(e.target.value)} type="date" data-testid="cs-observed" className="mt-1 w-full bg-white border border-black/15 rounded px-3 py-2 text-sm" />
              <div className="mt-2">
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="cs-category" className="mt-1 w-full bg-white border border-black/15 rounded px-2 py-1.5 text-sm">
                  <option value="">Auto-detect</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.emoji || ""} {c.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Tags (comma-separated)</label>
            <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="drought, kerala, mango" data-testid="cs-tags" className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
          </div>

          {/* Submit */}
          <div className="pt-2 flex items-center gap-3">
            <button
              disabled={submitting}
              onClick={submit}
              data-testid="cs-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-6 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
            >
              <CheckCircle size={13} weight="fill" /> {submitting ? "Publishing…" : "Publish story"}
            </button>
            {!user && <div className="text-xs text-[#3A3830]">Sign in required.</div>}
          </div>
        </div>
      </div>
    </main>
  );
}
